import React, { useState, useRef } from "react";
import { Button, Typography, Slider, Flex } from "antd";
import {
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowTurnRightDownIcon,
} from "@heroicons/react/24/outline";

const { Text } = Typography;

interface SpecimenImageProps {
  imgUrl?: string;
}

const SpecimenImage: React.FC<SpecimenImageProps> = ({ imgUrl }) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });

  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [rotation, setRotation] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ scale, offset });

  React.useEffect(() => {
    stateRef.current = { scale, offset };
  }, [scale, offset]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleNativeWheel = (e: WheelEvent) => {
      e.preventDefault(); // Crucial: prevents the browser from zooming the whole page

      const { scale: currentScale, offset: currentOffset } = stateRef.current;
      let newScale = currentScale;

      if (e.deltaY < 0) {
        newScale = Math.min(currentScale + 0.2, 5);
      } else {
        newScale = Math.max(currentScale - 0.2, 1);
      }

      if (newScale === currentScale) return;

      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left - rect.width / 2;
      const cursorY = e.clientY - rect.top - rect.height / 2;

      const scaleRatio = newScale / currentScale;

      const newOffsetX = cursorX - (cursorX - currentOffset.x) * scaleRatio;
      const newOffsetY = cursorY - (cursorY - currentOffset.y) * scaleRatio;

      setScale(newScale);
      if (newScale === 1) {
        setOffset({ x: 0, y: 0 });
      } else {
        setOffset({ x: newOffsetX, y: newOffsetY });
      }
    };

    // { passive: false } is required to allow e.preventDefault() inside a wheel event handler
    container.addEventListener("wheel", handleNativeWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleNativeWheel);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStartPos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      const deltaX = e.clientX - dragStartPos.x;
      const deltaY = e.clientY - dragStartPos.y;
      setOffset((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      setDragStartPos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleZoomIn = () => {
    const { scale: currentScale, offset: currentOffset } = stateRef.current;
    const newScale = Math.min(currentScale + 0.2, 5);
    if (newScale === currentScale) return;
    const scaleRatio = newScale / currentScale;
    setScale(newScale);
    setOffset({
      x: currentOffset.x * scaleRatio,
      y: currentOffset.y * scaleRatio,
    });
  };

  const handleZoomOut = () => {
    const { scale: currentScale, offset: currentOffset } = stateRef.current;
    const newScale = Math.max(currentScale - 0.2, 1);
    setScale(newScale);
    if (newScale === 1) {
      setOffset({ x: 0, y: 0 });
    } else {
      const scaleRatio = newScale / currentScale;
      setOffset({
        x: currentOffset.x * scaleRatio,
        y: currentOffset.y * scaleRatio,
      });
    }
  };

  const imageStyle: React.CSSProperties = {
    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
    filter: `brightness(${brightness}%) contrast(${contrast}%)`,
    cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
    transition: isDragging
      ? "none"
      : "transform 0.1s ease-out, filter 0.1s ease-out",
    userSelect: "none",
    pointerEvents: "none",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  };

  return (
    <Flex vertical gap="middle" align="center">
      <Flex gap="middle" align="center" className="w-full justify-center">
        <div
          ref={containerRef}
          className="relative overflow-hidden inline-flex bg-gray-50 border rounded-lg items-center justify-center"
          style={{
            maxHeight: "600px",
            maxWidth: "800px",
            minWidth: "600px",
            minHeight: "600px",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
        >
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Specimen"
              style={imageStyle}
              draggable={false}
              crossOrigin="anonymous"
            />
          ) : (
            <Text type="secondary">No image available</Text>
          )}
        </div>

        {/* Controls on Right */}
        <Flex vertical gap="8px">
          <Button
            shape="circle"
            type="primary"
            className="flex items-center justify-center"
            aria-label="Rotate image 90 degrees"
            icon={
              <div className="flex items-center justify-center w-full h-full">
                <ArrowTurnRightDownIcon className="w-5 h-5" />
              </div>
            }
            onClick={handleRotate}
            title="Rotate 90°"
          />
          <Button
            shape="circle"
            type="primary"
            className="flex items-center justify-center"
            aria-label="Zoom in"
            icon={
              <div className="flex items-center justify-center w-full h-full">
                <MagnifyingGlassPlusIcon className="w-5 h-5" />
              </div>
            }
            onClick={handleZoomIn}
            title="Zoom In"
          />
          <Button
            shape="circle"
            type="primary"
            className="flex items-center justify-center"
            aria-label="Zoom out"
            icon={
              <div className="flex items-center justify-center w-full h-full">
                <MagnifyingGlassMinusIcon className="w-5 h-5" />
              </div>
            }
            onClick={handleZoomOut}
            title="Zoom Out"
          />
        </Flex>
      </Flex>

      {/* Sliders Below Image */}
      <Flex
        gap="large"
        justify="center"
        align="center"
        className="px-4 w-full mt-4 bg-transparent slider-handle"
      >
        <Flex vertical align="center" className="min-w-50">
          <Slider
            className="w-full m-0 mb-2 "
            min={0}
            max={200}
            value={brightness}
            onChange={setBrightness}
            tooltip={{ formatter: (v) => `${v}%` }}
          />
          <Text className="text-[#0C4A6E] text-xl">Brightness</Text>
        </Flex>
        <Flex vertical align="center" className="min-w-50">
          <Slider
            className="w-full m-0 mb-2"
            min={0}
            max={200}
            value={contrast}
            onChange={setContrast}
            tooltip={{ formatter: (v) => `${v}%` }}
          />
          <Text className="text-[#0C4A6E] text-xl">Contrast</Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SpecimenImage;
